// WP-1.15 (RE-4, S-09 export): a minimal, dependency-free `.xlsx` writer. The locked toolchain
// (DEV_PLAN §2) lists no spreadsheet library and calls out "no heavy libs" (custom Money module),
// so — exactly as with the custom Money core and the hand-rolled CSV parser (csv.ts) — this hand-
// builds the smallest valid OOXML workbook: inline-string + numeric cells only, no styles/shared-
// strings/formulas (a full-fidelity workbook is a later concern). Deterministic (fixed zip
// timestamps → same bytes for same input, so it's byte-testable), pure, no DOM/Node APIs beyond
// the global TextEncoder.
import type { TabularCell, TabularData } from './exportTable';

// --- CRC-32 (IEEE 802.3), needed for each ZIP entry ---------------------------------------
let crcTable: Uint32Array | undefined;
function getCrcTable(): Uint32Array {
  if (crcTable !== undefined) return crcTable;
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) === 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  crcTable = table;
  return table;
}

export function crc32(bytes: Uint8Array): number {
  const table = getCrcTable();
  let crc = 0xffffffff;
  for (const byte of bytes) {
    // index is always 0..255; the `?? 0` only satisfies noUncheckedIndexedAccess.
    crc = (table[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

// --- little-endian byte writer -------------------------------------------------------------
class ByteWriter {
  private readonly bytes: number[] = [];
  get length(): number {
    return this.bytes.length;
  }
  u16(value: number): void {
    this.bytes.push(value & 0xff, (value >>> 8) & 0xff);
  }
  u32(value: number): void {
    this.bytes.push(
      value & 0xff,
      (value >>> 8) & 0xff,
      (value >>> 16) & 0xff,
      (value >>> 24) & 0xff,
    );
  }
  raw(data: Uint8Array): void {
    for (const byte of data) this.bytes.push(byte);
  }
  toUint8Array(): Uint8Array {
    return Uint8Array.from(this.bytes);
  }
}

export interface ZipPart {
  name: string;
  data: Uint8Array;
}

/** A store-only (method 0, no compression) ZIP — valid and read by Excel/any unzip; avoids a
 * deflate impl. Reused by `toXlsx` and by BK-2's "export everything" bundle (WP-1.20). */
export function zipFiles(parts: readonly ZipPart[]): Uint8Array {
  const encoder = new TextEncoder();
  const writer = new ByteWriter();
  const central: { name: Uint8Array; crc: number; size: number; offset: number }[] = [];

  for (const part of parts) {
    const nameBytes = encoder.encode(part.name);
    const crc = crc32(part.data);
    const offset = writer.length;
    writer.u32(0x04034b50); // local file header signature
    writer.u16(20); // version needed
    writer.u16(0); // flags
    writer.u16(0); // method: stored
    writer.u16(0); // mod time (fixed for determinism)
    writer.u16(0); // mod date (fixed for determinism)
    writer.u32(crc);
    writer.u32(part.data.length); // compressed size
    writer.u32(part.data.length); // uncompressed size
    writer.u16(nameBytes.length);
    writer.u16(0); // extra length
    writer.raw(nameBytes);
    writer.raw(part.data);
    central.push({ name: nameBytes, crc, size: part.data.length, offset });
  }

  const centralStart = writer.length;
  for (const entry of central) {
    writer.u32(0x02014b50); // central directory header signature
    writer.u16(20); // version made by
    writer.u16(20); // version needed
    writer.u16(0); // flags
    writer.u16(0); // method
    writer.u16(0); // time
    writer.u16(0); // date
    writer.u32(entry.crc);
    writer.u32(entry.size);
    writer.u32(entry.size);
    writer.u16(entry.name.length);
    writer.u16(0); // extra length
    writer.u16(0); // comment length
    writer.u16(0); // disk number start
    writer.u16(0); // internal attributes
    writer.u32(0); // external attributes
    writer.u32(entry.offset);
    writer.raw(entry.name);
  }
  const centralSize = writer.length - centralStart;

  writer.u32(0x06054b50); // end of central directory signature
  writer.u16(0); // this disk
  writer.u16(0); // disk with central directory
  writer.u16(central.length); // entries on this disk
  writer.u16(central.length); // total entries
  writer.u32(centralSize);
  writer.u32(centralStart);
  writer.u16(0); // comment length
  return writer.toUint8Array();
}

// --- OOXML parts ---------------------------------------------------------------------------
const XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

const CONTENT_TYPES =
  `${XML_HEADER}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
  '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
  '</Types>';

const ROOT_RELS =
  `${XML_HEADER}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
  '</Relationships>';

const WORKBOOK_RELS =
  `${XML_HEADER}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
  '</Relationships>';

function xmlEscape(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function attrEscape(text: string): string {
  return xmlEscape(text).replace(/"/g, '&quot;');
}

// Excel: sheet names are ≤31 chars and may not contain \ / ? * [ ] :
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[\\/?*[\]:]/g, ' ').trim();
  return (cleaned === '' ? 'Sheet1' : cleaned).slice(0, 31);
}

function workbookXml(sheetName: string): string {
  return (
    `${XML_HEADER}<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<sheets><sheet name="${attrEscape(sanitizeSheetName(sheetName))}" sheetId="1" r:id="rId1"/></sheets>` +
    '</workbook>'
  );
}

function columnRef(index0: number): string {
  let n = index0;
  let ref = '';
  do {
    ref = String.fromCharCode(65 + (n % 26)) + ref;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return ref;
}

function cellXml(ref: string, value: TabularCell | undefined): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}"><v>${String(value)}</v></c>`;
  }
  const text = value === undefined ? '' : String(value);
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(text)}</t></is></c>`;
}

function rowXml(rowNumber: number, values: readonly (TabularCell | undefined)[]): string {
  const cells = values.map((value, i) => cellXml(columnRef(i) + String(rowNumber), value)).join('');
  return `<row r="${String(rowNumber)}">${cells}</row>`;
}

function sheetXml(data: TabularData): string {
  const rows: string[] = [];
  let rowNumber = 1;
  rows.push(
    rowXml(
      rowNumber,
      data.columns.map((c) => c.label),
    ),
  );
  rowNumber += 1;
  for (const row of data.rows) {
    rows.push(
      rowXml(
        rowNumber,
        data.columns.map((c) => row[c.key]),
      ),
    );
    rowNumber += 1;
  }
  if (data.footnotes !== undefined && data.footnotes.length > 0) {
    rowNumber += 1; // leave one blank row as a visual separator
    for (const footnote of data.footnotes) {
      rows.push(rowXml(rowNumber, [footnote]));
      rowNumber += 1;
    }
  }
  return (
    `${XML_HEADER}<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<sheetData>${rows.join('')}</sheetData></worksheet>`
  );
}

export function toXlsx(sheetName: string, data: TabularData): Uint8Array {
  const encoder = new TextEncoder();
  const parts: ZipPart[] = [
    { name: '[Content_Types].xml', data: encoder.encode(CONTENT_TYPES) },
    { name: '_rels/.rels', data: encoder.encode(ROOT_RELS) },
    { name: 'xl/workbook.xml', data: encoder.encode(workbookXml(sheetName)) },
    { name: 'xl/_rels/workbook.xml.rels', data: encoder.encode(WORKBOOK_RELS) },
    { name: 'xl/worksheets/sheet1.xml', data: encoder.encode(sheetXml(data)) },
  ];
  return zipFiles(parts);
}
